<template>
    <section class="flex flex-col gap-5">
        <BaseButton theme="tertiary" size="sm" class="self-start" :disabled="deleting" @click="emit('back')">
            <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("common.back") }}
        </BaseButton>

        <div v-if="loading" class="scaffold-body-small text-ink-muted">{{ t("reflectionsAdmin.panel.loading") }}</div>
        <div v-else-if="loadError" class="flex flex-col items-start gap-3">
            <BaseNotification theme="error" :text="loadError" hide-dismiss class="self-stretch" />
            <BaseButton theme="neutral" size="sm" @click="loadCycle">{{ t("common.tryAgain") }}</BaseButton>
        </div>
        <template v-else-if="cycle">
            <header class="flex flex-col gap-2">
                <div class="flex items-center gap-3 flex-wrap">
                    <ReflectionIcon kind="sixty" size="md" />
                    <h2 class="title-subsection text-ink">{{ headerTitle }}</h2>
                    <BaseChip v-if="mode === 'draft'" size="2xs">{{ t("reflections.hub.step", { current: 2, total: 3 }) }}</BaseChip>
                </div>
                <p class="scaffold-body-small text-ink-muted">{{ subtitle }}</p>
            </header>

            <HorizontalDivider as="hr" spacing="none" />

            <AssessmentThresholdsCallout
                v-if="thresholds !== null && mode !== 'read-only'"
                :progress="thresholds"
                :show-invited="showInvitedCounts"
                :show-submitted="showSubmittedCounts" />

            <div v-else-if="mode === 'draft'" class="flex flex-col items-start gap-2">
                <StatusChip state="pending" size="2xs" class="self-start"> {{ addedChipLabel }}</StatusChip>
            </div>

            <BaseCard v-if="invites.length > 0" padding="none">
                <div class="px-4 py-3 border-b border-border body-semibold text-ink-muted">{{ t("reflections.sixty.nominate.invited") }}</div>
                <ul class="divide-y divide-border">
                    <li v-for="invite in invites" :key="invite.id" class="px-4 py-3 flex flex-wrap items-center gap-3">
                        <PeopleAvatar size="sm" :name="invite.rater.display_name" />
                        <div class="flex-1 min-w-32">
                            <div class="body-semibold text-ink truncate">{{ invite.rater.display_name }}</div>
                            <div class="scaffold-details-small text-ink-muted truncate">{{ invite.rater.email }}</div>
                            <div v-if="invite.last_reminder_at" class="scaffold-details-small text-ink-muted">
                                {{ t("reflections.sixty.nominate.lastReminded", { date: formatInviteNotificationDate(invite.last_reminder_at) }) }}
                            </div>
                            <div v-else-if="invite.invited_at" class="scaffold-details-small text-ink-muted">
                                {{ t("reflections.sixty.nominate.requestedReview", { date: formatInviteNotificationDate(invite.invited_at) }) }}
                            </div>
                        </div>
                        <div class="flex items-center gap-3 ml-auto">
                            <BaseChip v-if="invite.relationship" size="2xs">{{ relationshipLabel(invite.relationship) }}</BaseChip>
                            <BaseButton
                                v-if="canActOnInvite(cycle, invite) && mode === 'add-more'"
                                type="button"
                                theme="neutral"
                                size="sm"
                                class="inline-flex items-center gap-2"
                                :disabled="resendingId === invite.id || isResendDisabled(invite)"
                                :title="isResendDisabled(invite) ? t('reflections.sixty.nominate.resendLimit') : t('reflections.sixty.nominate.resendTitle')"
                                @click="resendInvite(invite)">
                                <i
                                    class="bi"
                                    :class="resendingId === invite.id ? 'bi-hourglass-split' : resentIds.has(invite.id) ? 'bi-check2' : 'bi-envelope'"
                                    aria-hidden="true"></i>
                                {{
                                    resendingId === invite.id
                                        ? t("reflections.sixty.nominate.sending")
                                        : resentIds.has(invite.id)
                                          ? t("reflections.sixty.nominate.sent")
                                          : t("reflections.sixty.nominate.resend")
                                }}
                            </BaseButton>
                            <AssessmentInviteCopyMenu v-if="invite.invite_token && mode !== 'draft'" :invite="invite" :cycle="cycle" />
                            <StatusChip v-if="invite.state" :state="invite.state === 'responded' ? 'done' : 'pending'" size="2xs">
                                {{ inviteStateLabel(invite.state) }}
                            </StatusChip>
                            <BaseButton
                                v-if="canRemove(invite)"
                                type="button"
                                theme="tertiary"
                                size="sm"
                                icon-only
                                :title="t('reflections.sixty.nominate.remove')"
                                :disabled="deleting"
                                @click="requestRemoveInvite(invite.id)">
                                <i class="bi bi-x-lg" aria-hidden="true"></i>
                            </BaseButton>
                        </div>
                    </li>
                </ul>
            </BaseCard>

            <BaseCard v-if="mode !== 'read-only'" padding="md">
                <div class="flex flex-col gap-2">
                    <span class="body-semibold text-ink">{{ t("reflections.sixty.nominate.addRater.title") }}</span>
                    <span class="scaffold-body-small text-ink-muted mb-1"> {{ t("reflections.sixty.nominate.addRater.hint") }} </span>
                    <span class="flex flex-col gap-2 md:flex-row md:items-end">
                        <label for="rater-search" class="relative block md:flex-1 md:min-w-0">
                            <span class="sr-only">{{ t("reflections.sixty.nominate.addRater.search") }}</span>
                            <input
                                id="rater-search"
                                ref="searchInputEl"
                                v-model="query"
                                :placeholder="t('reflections.sixty.nominate.addRater.search')"
                                class="w-full px-3 py-2 border border-control-border rounded-lgr outline-none focus:border-control-border-focus scaffold-body-small"
                                @input="onQueryChange"
                                @focus="onQueryChange"
                                @keydown="handleKeydown" />
                            <Teleport to="body">
                                <ul
                                    v-if="options.length > 0"
                                    class="fixed z-50 bg-surface-raised border border-border-strong rounded-lgr shadow-lg max-h-64 overflow-y-auto"
                                    :style="dropdownStyle">
                                    <li v-for="(option, index) in options" :key="option.kind === 'user' ? option.rater.uuid : `external:${option.email}`">
                                        <button
                                            :ref="(el) => setItemRef(el, index)"
                                            type="button"
                                            class="w-full text-left px-3 py-2 cursor-pointer flex items-center gap-2"
                                            :class="{ 'bg-surface-sunken': index === highlightedIndex }"
                                            @click="selectOption(option)"
                                            @mouseenter="highlightedIndex = index"
                                            @mouseleave="highlightedIndex = -1"
                                            @focus="highlightedIndex = index"
                                            @blur="highlightedIndex = -1">
                                            <template v-if="option.kind === 'user'">
                                                <PeopleAvatar size="xs" :name="option.rater.display_name" />
                                                <div class="flex-1 min-w-0">
                                                    <div class="body-semibold text-ink truncate">{{ option.rater.display_name }}</div>
                                                    <div class="scaffold-details-small text-ink-muted truncate">{{ option.rater.email }}</div>
                                                </div>
                                            </template>
                                            <template v-else>
                                                <BaseAvatar size="xs"><i class="bi bi-envelope text-xs" aria-hidden="true"></i></BaseAvatar>
                                                <div class="flex-1 min-w-0">
                                                    <div class="body-semibold text-ink truncate">{{ option.email }}</div>
                                                    <div class="scaffold-details-small text-ink-muted">{{ t("reflections.sixty.nominate.addRater.external") }}</div>
                                                </div>
                                            </template>
                                        </button>
                                    </li>
                                </ul>
                            </Teleport>
                        </label>
                        <span v-if="subjectSpecifiesRelationship" class="flex flex-col gap-1 md:w-52 md:shrink-0">
                            <span class="scaffold-details-small text-ink-muted">{{ t("reflections.sixty.nominate.relationship.label") }}</span>
                            <BaseSelect
                                v-model="selectedRelationship"
                                :options="relationshipOptions"
                                :placeholder="t('reflections.sixty.nominate.relationship.placeholder')"
                                :aria-label="t('reflections.sixty.nominate.relationship.label')"
                                trigger-class="h-9.5 py-0" />
                        </span>
                        <BaseTooltip as-child theme="dark" :disabled="!hasRecipient || !missingRelationship">
                            <template #trigger>
                                <span class="md:shrink-0">
                                    <BaseButton theme="primary" size="sm" class="md:h-9.5" :disabled="!canInvite" @click="submitInvite">
                                        {{ inviting ? t("reflections.sixty.nominate.inviting") : t("reflections.sixty.nominate.invite") }}
                                    </BaseButton>
                                </span>
                            </template>
                            <template #content>{{ t("reflections.sixty.nominate.relationship.required") }}</template>
                        </BaseTooltip>
                    </span>
                    <span v-if="stagedLabel" class="scaffold-details-small text-ink-muted flex items-center gap-2">
                        <i class="bi bi-check-circle-fill text-notification-success-text" aria-hidden="true"></i>
                        <I18nT v-if="staged?.kind === 'external' && missingRelationship" keypath="reflections.sixty.nominate.staged.external.needsRelationship" tag="span">
                            <template #name>
                                <span class="text-ink">{{ stagedLabel }}</span>
                            </template>
                        </I18nT>
                        <I18nT v-else-if="staged?.kind === 'external'" keypath="reflections.sixty.nominate.staged.external.ready" tag="span">
                            <template #name>
                                <span class="text-ink">{{ stagedLabel }}</span>
                            </template>
                        </I18nT>
                        <I18nT v-else-if="missingRelationship" keypath="reflections.sixty.nominate.staged.user.needsRelationship" tag="span">
                            <template #name>
                                <span class="text-ink">{{ stagedLabel }}</span>
                            </template>
                        </I18nT>
                        <I18nT v-else keypath="reflections.sixty.nominate.staged.user.ready" tag="span">
                            <template #name>
                                <span class="text-ink">{{ stagedLabel }}</span>
                            </template>
                        </I18nT>
                        <BaseButton theme="tertiary" size="sm" @click="clearStaged">{{ t("reflections.sixty.nominate.change") }}</BaseButton>
                    </span>
                </div>
            </BaseCard>

            <BaseCard v-if="cycle.personal_note" theme="assessment" padding="md" class="flex flex-col gap-1">
                <div class="body-semibold text-ink">{{ t("reflections.sixty.nominate.noteToRaters") }}</div>
                <p class="scaffold-body-small text-ink-muted">{{ t("reflections.sixty.nominate.quotedNote", { note: cycle.personal_note }) }}</p>
            </BaseCard>

            <BaseNotification v-if="error" theme="warning" :text="error" @dismiss="error = null" />

            <div v-if="mode === 'draft'" class="flex items-center justify-between pt-2 gap-3 flex-wrap">
                <div class="flex items-center gap-3 flex-wrap">
                    <span class="scaffold-body-small text-ink-muted">{{ t("reflections.sixty.nominate.nextStep") }}</span>
                    <BaseButton v-if="!cycle?.is_campaign_cycle" theme="tertiary" size="sm" :disabled="deleting || launching" @click="isConfirmDeleteDialogOpen = true">
                        {{ t("reflections.sixty.nominate.deleteCycle") }}
                    </BaseButton>
                </div>
                <BaseTooltip as-child theme="dark" :disabled="canLaunch">
                    <template #trigger>
                        <span>
                            <BaseButton theme="primary" :disabled="!canLaunch || launching || deleting" @click="onLaunch">
                                {{ launching ? t("reflections.sixty.nominate.launching") : t("reflections.sixty.nominate.launch") }}
                            </BaseButton>
                        </span>
                    </template>
                    <template #content>{{ launchBlockedReason }}</template>
                </BaseTooltip>
            </div>
            <div v-else class="flex items-center justify-end pt-2">
                <BaseButton type="button" theme="primary" @click="emit('back')">{{ t("common.done") }}</BaseButton>
            </div>
        </template>
    </section>
    <ConfirmationDialog
        v-model:open="isConfirmRemoveDialogOpen"
        :title="t('reflections.sixty.nominate.removeDialog.title')"
        :text="t('reflections.sixty.nominate.removeDialog.text')"
        :confirm-label="t('reflections.sixty.nominate.remove')"
        @confirm="confirmRemoveInvite" />
    <ConfirmationDialog
        v-model:open="isConfirmDeleteDialogOpen"
        :title="t('reflections.shared.deleteCycle.title360')"
        :text="t('reflections.shared.deleteCycle.bodyDraft')"
        :confirm-label="t('common.delete')"
        confirm-theme="danger"
        :confirm-disabled="deleting"
        :cancel-disabled="deleting"
        :close-on-confirm="false"
        @confirm="onConfirmDelete" />
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type {
    CycleEnvelopeResponse,
    CycleForSubject,
    InviteAnonymized,
    InviteEnvelopeResponse,
    ReflectionResponseRelationship,
    SearchRatersResponse,
    UserMinimal,
} from "~vue/api/generated/reflections";
import AssessmentInviteCopyMenu from "~vue/components/AssessmentInviteCopyMenu.vue";
import AssessmentThresholdsCallout from "~vue/components/AssessmentThresholdsCallout.vue";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import PeopleAvatar from "~vue/components/PeopleAvatar.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { useAssessmentInviteResend } from "~vue/composables/useAssessmentInviteResend";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { withLiveNominations } from "~vue/utils/categoryThresholds";
import { formatNumber } from "~vue/utils/formatNumber";
import { isImeComposing } from "~vue/utils/keyboard";
import { logUserInteraction } from "~vue/utils/logUtils";
import { canActOnInvite, DEFAULT_RATER_RELATIONSHIPS, formatDate, inviteStateLabel, isNetworkError, isRelationshipRejected, raterRelationshipLabel } from "~vue/utils/threeSixty";
import type { ComponentPublicInstance } from "vue";
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { I18nT, useI18n } from "vue-i18n";

interface Props {
    cycleId: string;
}

const { cycleId } = defineProps<Props>();

const { t } = useI18n();

const emit = defineEmits<{
    (event: "back"): void;
    // Fired after the DRAFT cycle is launched. Payload lets the parent chain
    // directly into the take-self flow when the subject also wants to fill
    // out the self-assessment side of their own 360.
    (event: "launched", payload: { cycleId: string; selfAssessEnabled: boolean; title: string; closesAt: string | null }): void;
    // Fired after the DRAFT cycle is deleted; the view is spent.
    (event: "deleted", cycleId: string): void;
}>();

const isConfirmDeleteDialogOpen = ref(false);
const deleting = ref(false);

// The confirm dialog stays open (and can't be dismissed) until the DELETE
// settles, so Back, Invite rater and Remove can't race the pending delete.
async function onConfirmDelete() {
    // Campaign cycles belong to the org (the API refuses the delete), and a
    // launch on the wire is about to invite raters, so neither may be deleted.
    if (deleting.value || launching.value || mode.value !== "draft" || cycle.value?.is_campaign_cycle) {
        return;
    }
    deleting.value = true;
    // 204 with no body on success, so don't ask for JSON.
    const { error: fetchError } = await useFetch(`/v1/reflections/cycles/${cycleId}`).delete();
    deleting.value = false;
    isConfirmDeleteDialogOpen.value = false;
    if (fetchError.value) {
        error.value = fetchError.value.message || t("reflections.sixty.nominate.deleteError");
        return;
    }
    emit("deleted", cycleId);
}

const cycle = ref<CycleForSubject | null>(null);
const loading = ref(true);
const loadError = ref<string | null>(null);
const invites = ref<InviteAnonymized[]>([]);
const query = ref("");
const searchResults = ref<UserMinimal[]>([]);
const highlightedIndex = ref(-1);
const itemRefs = ref<HTMLElement[]>([]);

// Dropdown is teleported to <body> so a scrollable / overflow-hidden ancestor
// can't clip it. Position tracks the input's viewport rect and updates on
// window resize, scroll (capture — catches nested scrolls too), and whenever
// the option list opens.
const searchInputEl = ref<HTMLInputElement | null>(null);
const dropdownStyle = reactive({ top: "0px", left: "0px", width: "0px" });

function updateDropdownPosition() {
    const el = searchInputEl.value;
    if (!el) {
        return;
    }
    const rect = el.getBoundingClientRect();
    dropdownStyle.top = `${rect.bottom + 4}px`;
    dropdownStyle.left = `${rect.left}px`;
    dropdownStyle.width = `${rect.width}px`;
}
const launching = ref(false);
const error = ref<string | null>(null);

const subjectSpecifiesRelationship = computed(() => cycle.value?.subject_specifies_relationship ?? false);
const selectedRelationship = ref<ReflectionResponseRelationship | null>(null);
const relationshipOptions = computed(() =>
    cycle.value?.relationships
        ? cycle.value.relationships.map((option) => ({ key: option.value, label: option.label }))
        : DEFAULT_RATER_RELATIONSHIPS.map((option) => ({ key: option.value, label: raterRelationshipLabel(option.value) })),
);

// An admin can change the organization's categories while this page is open, and
// the server validates the nomination against the current list. Re-read them after
// a rejection so the next attempt offers what's actually accepted.
async function refreshRelationshipOptions() {
    const { data } = await useFetch(`/v1/reflections/cycles/${cycleId}`).json<CycleEnvelopeResponse>();
    const refreshed = data.value?.cycle?.relationships;
    if (refreshed && cycle.value) {
        cycle.value = { ...cycle.value, relationships: refreshed };
    }
}

function relationshipLabel(relationship: ReflectionResponseRelationship): string {
    return relationshipOptions.value.find((o) => o.key === relationship)?.label ?? relationship;
}

function requireRelationship(): boolean {
    if (subjectSpecifiesRelationship.value && selectedRelationship.value === null) {
        error.value = t("reflections.sixty.nominate.relationship.requiredBeforeAdding");
        return false;
    }
    return true;
}
const inviting = ref(false);
const isValidEmail = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(query.value.trim()));

// The dropdown is a single list of Nadia-user matches plus, when the input is
// an email with no exact Nadia match, an "invite external" option at the end.
type RaterOption = { kind: "user"; rater: UserMinimal } | { kind: "external"; email: string };

// Someone the subject picked but hasn't invited yet. The dropdown only stages a
// choice — it closes, the pick is echoed back, and Invite rater submits it.
const staged = ref<RaterOption | null>(null);
const hasRecipient = computed(() => staged.value !== null || isValidEmail.value);
const missingRelationship = computed(() => subjectSpecifiesRelationship.value && selectedRelationship.value === null);
const canInvite = computed(() => !inviting.value && !deleting.value && hasRecipient.value && !missingRelationship.value);

// What the search box holds while a pick is staged; typing anything else drops it.
const stagedQuery = computed(() => {
    if (staged.value === null) {
        return null;
    }
    return staged.value.kind === "user" ? staged.value.rater.display_name : staged.value.email;
});

const stagedLabel = computed(() => {
    if (staged.value === null) {
        return null;
    }
    return staged.value.kind === "user" ? `${staged.value.rater.display_name} (${staged.value.rater.email})` : staged.value.email;
});

const options = computed<RaterOption[]>(() => {
    if (staged.value !== null) {
        return [];
    }
    const opts: RaterOption[] = searchResults.value.map((rater) => ({ kind: "user", rater }));
    const trimmed = query.value.trim();
    if (isValidEmail.value && !searchResults.value.some((r) => r.email.toLowerCase() === trimmed.toLowerCase())) {
        opts.push({ kind: "external", email: trimmed });
    }
    return opts;
});

function setItemRef(el: Element | ComponentPublicInstance | null, index: number) {
    if (el instanceof HTMLElement) {
        itemRefs.value[index] = el;
    }
}

watch(options, () => {
    highlightedIndex.value = -1;
    itemRefs.value = [];
});

watch(highlightedIndex, async (index) => {
    if (index < 0) {
        return;
    }
    await nextTick();
    itemRefs.value[index]?.scrollIntoView({ block: "nearest" });
});

function canRemove(invite: InviteAnonymized) {
    return mode.value !== "read-only" && canActOnInvite(cycle.value, invite);
}

const pendingCount = computed(() => invites.value.filter((i) => i.state === "pending").length);
const thresholds = computed(() => {
    const stored = cycle.value?.category_thresholds ?? null;
    return stored === null ? null : withLiveNominations(stored, invites.value);
});
// Invites only carry a category when the subject picks it, so nomination
// progress is unknowable while raters categorize themselves.
const nominationProgress = computed(() => (subjectSpecifiesRelationship.value ? thresholds.value : null));
// Invites carry no category until the rater answers when raters categorize
// themselves, so those cycles have no per-category invite count to show.
const showInvitedCounts = computed(() => nominationProgress.value !== null);
// Responses only exist once the cycle is collecting, and they are what unlocks
// the report, so from then on they decide whether a category reads as met.
const showSubmittedCounts = computed(() => mode.value !== "draft");

const meetsMin = computed(() => pendingCount.value >= (cycle.value?.min_responses ?? 0));
// Category thresholds gate the report, not the launch: a subject short of a
// category still launches and keeps inviting while the cycle collects.
const canLaunch = computed(() => (thresholds.value === null ? meetsMin.value : pendingCount.value > 0));

// Only rendered for cycles without category thresholds; the thresholds callout
// carries the progress otherwise.
const addedChipLabel = computed(() => t("reflections.sixty.nominate.addedCount", { count: formatNumber(pendingCount.value), min: formatNumber(cycle.value?.min_responses ?? 0) }));

const launchBlockedReason = computed(() =>
    thresholds.value === null
        ? t("reflections.sixty.nominate.launchBlocked.minimum", { min: formatNumber(cycle.value?.min_responses ?? 0) })
        : t("reflections.sixty.nominate.launchBlocked.one"),
);

// `mode` drives all UI variants: DRAFT keeps the launch flow; COLLECTING /
// UNLOCKED cycles allow adding more raters; CLOSED cycles show read-only.
const mode = computed<"draft" | "add-more" | "read-only">(() => {
    if (cycle.value === null) {
        return "draft";
    }
    if (cycle.value.state === "draft") {
        return "draft";
    }
    if (cycle.value.state === "closed") {
        return "read-only";
    }
    return "add-more";
});

const closesAtLabel = computed<string | null>(() => formatDate(cycle.value?.closes_at, "short") || null);

const headerTitle = computed(() => {
    switch (mode.value) {
        case "draft":
            return t("reflections.sixty.nominate.title.draft");
        case "add-more":
            return t("reflections.sixty.nominate.title.addMore");
        default:
            return t("reflections.sixty.nominate.title.readOnly");
    }
});

const subtitle = computed(() => {
    if (!cycle.value) {
        return "";
    }
    if (mode.value === "draft") {
        if (thresholds.value !== null) {
            if (subjectSpecifiesRelationship.value) {
                return t("reflections.sixty.nominate.subtitle.thresholdsSubject");
            }
            return t("reflections.sixty.nominate.subtitle.thresholdsRater");
        }
        if (subjectSpecifiesRelationship.value) {
            return t("reflections.sixty.nominate.subtitle.minimumSubject", { min: formatNumber(cycle.value.min_responses) });
        }
        return t("reflections.sixty.nominate.subtitle.minimumRater", { min: formatNumber(cycle.value.min_responses) });
    }
    if (mode.value === "add-more") {
        return closesAtLabel.value ? t("reflections.sixty.nominate.subtitle.collectingUntil", { date: closesAtLabel.value }) : t("reflections.sixty.nominate.subtitle.collecting");
    }
    return t("reflections.sixty.nominate.subtitle.closed");
});

const { resendingId, resentIds, resendInvite, isResendDisabled, formatInviteNotificationDate } = useAssessmentInviteResend({
    cycleId,
    setError: (message) => {
        error.value = message;
    },
    onInviteUpdated: (inviteId, updatedInvite) => {
        const idx = invites.value.findIndex((invite) => invite.id === inviteId);
        if (idx !== -1) {
            invites.value[idx] = updatedInvite;
        }
    },
});

// Named so the error state can run it again. The failure it most often has to
// recover from is transient, and this panel lives inside the hub, so a reload
// would cost the user their place in it.
async function loadCycle() {
    loading.value = true;
    loadError.value = null;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${cycleId}`).json<CycleEnvelopeResponse>();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = isNetworkError(fetchError.value) ? t("reflections.shared.connection.loadFailed") : fetchError.value.message || t("reflections.sixty.nominate.loadError");
        return;
    }
    const loaded = data.value?.cycle;
    if (!loaded) {
        loadError.value = t("reflections.sixty.nominate.loadError");
        return;
    }
    if (loaded.is_self_assessment_only) {
        // Self-only cycles have no rater setup — just bounce back and let the
        // hub route the user into the take flow.
        emit("back");
        return;
    }
    cycle.value = loaded;
    invites.value = [...loaded.invites];
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_NOMINATE_VIEWED, {
        cycle_id: cycleId,
        current_invite_count: invites.value.length,
        min_responses: loaded.min_responses,
    });
}

onMounted(loadCycle);

onMounted(() => {
    globalThis.addEventListener("resize", updateDropdownPosition);
    // `capture: true` so we catch nested scroll containers too — the input can
    // live inside a scrollable card, and a plain listener on window would miss
    // those scrolls.
    globalThis.addEventListener("scroll", updateDropdownPosition, { capture: true, passive: true });
});

onUnmounted(() => {
    globalThis.removeEventListener("resize", updateDropdownPosition);
    globalThis.removeEventListener("scroll", updateDropdownPosition, { capture: true } as EventListenerOptions);
});

// Whenever the options list opens (transitions from empty to populated),
// re-measure the input so the teleported dropdown starts in the right spot.
watch(
    () => options.value.length > 0,
    (open) => {
        if (open) {
            void nextTick(updateDropdownPosition);
        }
    },
);

// The error notification renders just below the search field, where the
// teleported dropdown would sit on top of it.
watch(error, (message) => {
    if (message !== null) {
        staged.value = null;
        query.value = "";
        searchResults.value = [];
    }
});

let searchTimer: ReturnType<typeof setTimeout> | null = null;

function onQueryChange() {
    if (searchTimer) {
        clearTimeout(searchTimer);
    }
    if (staged.value && query.value !== stagedQuery.value) {
        staged.value = null;
    }
    const q = query.value.trim();
    if (q.length < 2) {
        searchResults.value = [];
        return;
    }
    searchTimer = setTimeout(async () => {
        const { data } = await useFetch(`/v1/reflections/raters/search?q=${encodeURIComponent(q)}&cycle_id=${cycleId}`).json<SearchRatersResponse>();
        searchResults.value = data.value?.results ?? [];
    }, 200);
}

function selectOption(option: RaterOption) {
    error.value = null;
    staged.value = option;
    query.value = option.kind === "user" ? option.rater.display_name : option.email;
    searchResults.value = [];
}

function clearStaged() {
    staged.value = null;
    query.value = "";
    searchResults.value = [];
    searchInputEl.value?.focus();
}

function submitInvite() {
    if (!canInvite.value) {
        return;
    }
    if (staged.value?.kind === "user") {
        void addInvite(staged.value.rater);
        return;
    }
    void addExternalInvite(staged.value?.kind === "external" ? staged.value.email : query.value.trim());
}

function handleKeydown(e: KeyboardEvent) {
    // Every key here, arrows included, belongs to the IME candidate list while composing.
    if (isImeComposing(e)) {
        return;
    }
    if (options.value.length === 0) {
        return;
    }
    if (e.key === "ArrowDown") {
        e.preventDefault();
        highlightedIndex.value = (highlightedIndex.value + 1) % options.value.length;
    } else if (e.key === "ArrowUp") {
        e.preventDefault();
        highlightedIndex.value = (highlightedIndex.value - 1 + options.value.length) % options.value.length;
    } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightedIndex.value >= 0) {
            selectOption(options.value[highlightedIndex.value]);
        } else {
            // No explicit highlight: Enter submits whatever is already staged or
            // typed, but never auto-picks the first name match.
            submitInvite();
        }
    } else if (e.key === "Escape") {
        e.preventDefault();
        searchResults.value = [];
    }
}

async function addInvite(rater: UserMinimal) {
    if (inviting.value || !requireRelationship()) {
        return;
    }
    inviting.value = true;
    error.value = null;
    const relationship = selectedRelationship.value;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${cycleId}/invites`)
        .post(JSON.stringify({ rater_uuid: rater.uuid, relationship }), "application/json")
        .json<InviteEnvelopeResponse>();
    finishSubmission();

    if (fetchError.value) {
        error.value = fetchError.value.message || t("reflections.sixty.nominate.addError");
        if (isRelationshipRejected(data.value)) {
            await refreshRelationshipOptions();
        }
        return;
    }

    const invite = data.value?.invite;
    if (!invite) {
        error.value = t("reflections.sixty.nominate.addError");
        return;
    }
    invites.value.push(invite);
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_ADDED, {
        cycle_id: cycleId,
        invite_count_after: invites.value.length,
    });
}

const isConfirmRemoveDialogOpen = ref(false);
const pendingRemoveInviteId = ref<string | null>(null);

function requestRemoveInvite(inviteId: string) {
    pendingRemoveInviteId.value = inviteId;
    isConfirmRemoveDialogOpen.value = true;
}

function confirmRemoveInvite() {
    const inviteId = pendingRemoveInviteId.value;
    pendingRemoveInviteId.value = null;
    if (inviteId) {
        void removeInvite(inviteId);
    }
}

async function removeInvite(inviteId: string) {
    const { error: fetchError } = await useFetch(`/v1/reflections/cycles/${cycleId}/invites/${inviteId}`).delete().json();
    if (fetchError.value) {
        error.value = t("reflections.sixty.nominate.removeError");
        return;
    }
    invites.value = invites.value.filter((i) => i.id !== inviteId);
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_REMOVED, {
        cycle_id: cycleId,
        invite_count_after: invites.value.length,
    });
}

async function addExternalInvite(email: string) {
    if (inviting.value || !requireRelationship()) {
        return;
    }
    inviting.value = true;
    error.value = null;
    const relationship = selectedRelationship.value;
    const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${cycleId}/external-invites`)
        .post(JSON.stringify({ rater_email: email, relationship }), "application/json")
        .json<InviteEnvelopeResponse>();
    finishSubmission();

    if (fetchError.value) {
        error.value = fetchError.value.message || t("reflections.sixty.nominate.inviteError");
        if (isRelationshipRejected(data.value)) {
            await refreshRelationshipOptions();
        }
        return;
    }
    const invite = data.value?.invite;
    if (!invite) {
        error.value = t("reflections.sixty.nominate.inviteError");
        return;
    }
    invites.value.push(invite);
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_ADDED, {
        cycle_id: cycleId,
        invite_count_after: invites.value.length,
        is_external: true,
    });
}

// Every submission — invited or rejected — empties the form, so the next rater
// starts from a blank relationship rather than inheriting the last one.
function finishSubmission() {
    inviting.value = false;
    staged.value = null;
    query.value = "";
    searchResults.value = [];
    selectedRelationship.value = null;
}

async function onLaunch() {
    if (!canLaunch.value || launching.value || deleting.value || mode.value !== "draft") {
        return;
    }
    launching.value = true;
    error.value = null;
    try {
        const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${cycleId}/launch`).post().json<CycleEnvelopeResponse>();
        if (fetchError.value) {
            error.value = fetchError.value.message || t("reflections.sixty.nominate.launchError");
            return;
        }
        emit("launched", {
            cycleId,
            selfAssessEnabled: cycle.value?.self_assess_enabled ?? false,
            title: cycle.value?.template.name ?? t("reflections.sixty.nominate.defaultTitle"),
            closesAt: data.value?.cycle.closes_at ?? null,
        });
    } finally {
        launching.value = false;
    }
}
</script>
